import React, { useState } from 'react';
import { attendanceAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Modal,
} from '../../UI';
import { ClipboardList, CheckCircle2, XCircle, AlertTriangle, Clock, User, Calendar, MessageSquare } from 'lucide-react';

const TeamRegularizationPage = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [markAsLOP, setMarkAsLOP] = useState(true);
  const [approvedStatus, setApprovedStatus] = useState('present');
  const [filter, setFilter] = useState('pending');

  // Bulk selection states
  const [selectedRegularizations, setSelectedRegularizations] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkComments, setBulkComments] = useState('');
  const [bulkApprovedStatus, setBulkApprovedStatus] = useState('present');
  const [bulkMarkAsLOP, setBulkMarkAsLOP] = useState(true);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const { data: allData, loading, refetch } = useApi(
    attendanceAPI.getPendingRegularizations,
    []
  );

  const { loading: actionLoading, execute: executeAction } = useAsyncAction();

  const allRequests = Array.isArray(allData) ? allData : (allData?.regularizations || []);
  
  // Filter requests based on selected filter
  const filteredRequests = allRequests.filter(req => {
    if (filter === 'pending') return req.status === 'pending';
    if (filter === 'approved') return req.status === 'approved';
    if (filter === 'rejected') return req.status === 'rejected';
    return true; // 'all' shows everything
  });

  const pendingCount = allRequests.filter(r => r.status === 'pending').length;

  const handleAction = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setComments('');
    setMarkAsLOP(true);
    setApprovedStatus(request.requestedStatus || 'present');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    try {
      setError('');
      let result;
      if (actionType === 'approve') {
        result = await executeAction(
          attendanceAPI.approveRegularization,
          selectedRequest._id,
          { comments, approvedStatus }
        );
      } else {
        if (!comments.trim()) {
          setError('Please provide a reason for rejection');
          return;
        }
        result = await executeAction(
          attendanceAPI.rejectRegularization,
          selectedRequest._id,
          { comments, markAsLOP }
        );
      }

      if (result.success) {
        setSuccess(`Regularization ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`);
        setShowActionModal(false);
        refetch();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || `Failed to ${actionType} regularization`);
      }
    } catch (err) {
      setError(err.message || 'Action failed');
    }
  };

  // Bulk approval handlers
  const toggleSelectRegularization = (regId) => {
    setSelectedRegularizations(prev => 
      prev.includes(regId) 
        ? prev.filter(id => id !== regId)
        : [...prev, regId]
    );
  };

  const toggleSelectAll = () => {
    const pendingRegs = filteredRequests.filter(r => r.status === 'pending');
    if (selectedRegularizations.length === pendingRegs.length) {
      setSelectedRegularizations([]);
    } else {
      setSelectedRegularizations(pendingRegs.map(r => r._id));
    }
  };

  const openBulkModal = (action) => {
    if (selectedRegularizations.length === 0) {
      setError('Please select at least one regularization request');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setBulkAction(action);
    setBulkComments('');
    setBulkApprovedStatus('present');
    setBulkMarkAsLOP(true);
    setShowBulkModal(true);
  };

  const handleBulkAction = async () => {
    if (bulkAction === 'reject' && !bulkComments.trim()) {
      setError('Please provide a reason for bulk rejection');
      return;
    }

    setBulkProcessing(true);
    try {
      let response;
      if (bulkAction === 'approve') {
        response = await attendanceAPI.bulkApproveRegularizations(selectedRegularizations, {
          comments: bulkComments,
          approvedStatus: bulkApprovedStatus
        });
      } else {
        response = await attendanceAPI.bulkRejectRegularizations(selectedRegularizations, {
          comments: bulkComments,
          markAsLOP: bulkMarkAsLOP
        });
      }

      const successCount = response.data?.success?.length || 0;
      const failedCount = response.data?.failed?.length || 0;

      setSuccess(`Bulk ${bulkAction}: ${successCount} successful, ${failedCount} failed`);
      setShowBulkModal(false);
      setSelectedRegularizations([]);
      setBulkComments('');
      refetch();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || `Failed to ${bulkAction} regularizations`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const getTypeBadge = (type) => {
    const colors = {
      'missed-punch-in': 'bg-orange-100 text-orange-700',
      'missed-punch-out': 'bg-orange-100 text-orange-700',
      'wrong-location': 'bg-blue-100 text-blue-700',
      'forgot-punch': 'bg-amber-100 text-amber-700',
      'other': 'bg-gray-100 text-gray-700',
    };
    const labels = {
      'missed-punch-in': 'Missed Punch In',
      'missed-punch-out': 'Missed Punch Out',
      'wrong-location': 'Wrong Location',
      'forgot-punch': 'Forgot Punch',
      'other': 'Other',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
        {labels[type] || type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-emerald-600" />
            Team Regularization
          </h2>
          <p className="text-gray-500 mt-1">Approve or reject attendance regularization requests</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedRegularizations.length > 0 && filter === 'pending' && (
            <>
              <span className="text-sm text-gray-600">
                {selectedRegularizations.length} selected
              </span>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => openBulkModal('approve')}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Bulk Approve
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => openBulkModal('reject')}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Bulk Reject
              </Button>
            </>
          )}
          {pendingCount > 0 && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
              <Clock className="w-4 h-4 mr-1" />
              {pendingCount} Pending
            </span>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} />}

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'pending'
              ? 'bg-amber-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Pending {pendingCount > 0 && `(${pendingCount})`}
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'approved'
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Approved
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'rejected'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Rejected
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-gray-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All History
        </button>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            {filter === 'pending' ? 'All caught up!' : 'No requests found'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {filter === 'pending' 
              ? 'No pending regularization requests from your team' 
              : `No ${filter} regularization requests`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filter === 'pending' && filteredRequests.length > 1 && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                checked={selectedRegularizations.length === filteredRequests.length}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 font-medium">
                Select All ({filteredRequests.length})
              </span>
            </div>
          )}
          {filteredRequests.map((req) => {
            const emp = req.employee || {};
            const isPending = req.status === 'pending';

            return (
              <div
                key={req._id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4"
              >
                <div className="flex items-start gap-3">
                  {isPending && (
                    <input
                      type="checkbox"
                      checked={selectedRegularizations.includes(req._id)}
                      onChange={() => toggleSelectRegularization(req._id)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      {/* Employee Info & Request Details */}
                      <div className="flex-1 space-y-3">
                        {/* Employee */}
                        <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                        {emp.name?.charAt(0)?.toUpperCase() || 'E'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{emp.name}</h3>
                        <p className="text-xs text-gray-500">{emp.employeeId} | {emp.designation}</p>
                      </div>
                    </div>

                    {/* Request Details */}
                    <div className="flex flex-wrap items-center gap-3">
                      {getTypeBadge(req.type || req.regularizationType)}
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(req.date || req.attendanceDate)}
                      </span>
                      {req.requestedStatus && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          req.requestedStatus === 'half_day' 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {req.requestedStatus === 'half_day' ? 'Half Day' : 'Full Day'}
                        </span>
                      )}
                    </div>

                    {/* Requested Times */}
                    {(req.requestedPunchIn || req.requestedPunchOut) && (
                      <div className="flex flex-wrap gap-4 text-sm">
                        {req.requestedPunchIn && (
                          <span className="text-gray-600">
                            <span className="text-gray-400">Punch In: </span>
                            {formatTime(req.requestedPunchIn)}
                          </span>
                        )}
                        {req.requestedPunchOut && (
                          <span className="text-gray-600">
                            <span className="text-gray-400">Punch Out: </span>
                            {formatTime(req.requestedPunchOut)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Reason */}
                    {req.reason && (
                      <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                        <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <p>{req.reason}</p>
                      </div>
                    )}

                    {/* Applied Date */}
                    <p className="text-xs text-gray-400">
                      Applied on {formatDate(req.createdAt)}
                    </p>

                    {/* Status Badge for non-pending requests */}
                    {req.status !== 'pending' && (
                      <div className="flex items-center gap-2">
                        {req.status === 'approved' ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Rejected
                          </span>
                        )}
                        {req.reviewedBy && (
                          <span className="text-xs text-gray-500">
                            by {req.reviewedBy.name}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Admin Comments */}
                    {req.adminComments && req.status !== 'pending' && (
                      <div className="flex items-start gap-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                        <MessageSquare className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-blue-600 font-medium mb-0.5">Admin Comments:</p>
                          <p>{req.adminComments}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons - Only show for pending requests */}
                  {isPending && (
                    <div className="flex sm:flex-col gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleAction(req, 'approve')}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors text-sm font-medium"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleAction(req, 'reject')}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors text-sm font-medium"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      </div>
    )}

      {/* Action Modal */}
      {showActionModal && selectedRequest && (
        <Modal
          isOpen={showActionModal}
          onClose={() => setShowActionModal(false)}
          title={actionType === 'approve' ? 'Approve Regularization' : 'Reject Regularization'}
        >
          <div className="space-y-4">
            {/* Request Summary */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-900">{selectedRequest.employee?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{formatDate(selectedRequest.date || selectedRequest.attendanceDate)}</span>
              </div>
              {getTypeBadge(selectedRequest.type || selectedRequest.regularizationType)}
              {selectedRequest.reason && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Reason:</span> {selectedRequest.reason}
                </p>
              )}
            </div>

            {actionType === 'approve' ? (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-sm text-green-800">This regularization will be approved and the attendance record will be updated.</p>
                  </div>
                </div>

                {/* Attendance Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mark Attendance As
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setApprovedStatus('present')}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        approvedStatus === 'present'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />
                      Full Day Present
                    </button>
                    <button
                      type="button"
                      onClick={() => setApprovedStatus('half_day')}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        approvedStatus === 'half_day'
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 inline-block mr-1.5" />
                      Half Day
                    </button>
                  </div>
                  {selectedRequest?.requestedStatus && (
                    <p className="text-xs text-gray-500 mt-1">
                      Employee requested: {selectedRequest.requestedStatus === 'half_day' ? 'Half Day' : 'Full Day'}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-800">This regularization will be rejected. Please provide a reason.</p>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={markAsLOP}
                    onChange={(e) => setMarkAsLOP(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Mark this day as Loss of Pay (LOP)</span>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments {actionType === 'reject' ? '(Required)' : '(Optional)'}
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={actionType === 'reject' ? 'Provide reason for rejection...' : 'Add any comments...'}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowActionModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant={actionType === 'approve' ? 'success' : 'danger'}
                onClick={confirmAction}
                loading={actionLoading}
                className="flex-1"
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <Modal
          isOpen={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          title={`Bulk ${bulkAction === 'approve' ? 'Approve' : 'Reject'} Regularizations`}
        >
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                You are about to {bulkAction} <span className="font-semibold">{selectedRegularizations.length}</span> regularization request(s).
              </p>
            </div>

            {bulkAction === 'approve' ? (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-sm text-green-800">These regularizations will be approved and attendance records will be updated.</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mark All Attendance As
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setBulkApprovedStatus('present')}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        bulkApprovedStatus === 'present'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />
                      Full Day Present
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkApprovedStatus('half_day')}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        bulkApprovedStatus === 'half_day'
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 inline-block mr-1.5" />
                      Half Day
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-800">These regularizations will be rejected. Please provide a reason.</p>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkMarkAsLOP}
                    onChange={(e) => setBulkMarkAsLOP(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Mark these days as Loss of Pay (LOP)</span>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments {bulkAction === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={bulkComments}
                onChange={(e) => setBulkComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={bulkAction === 'approve' ? 'Optional comments...' : 'Please provide a reason for rejection...'}
                required={bulkAction === 'reject'}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowBulkModal(false)}
                disabled={bulkProcessing}
              >
                Cancel
              </Button>
              <Button
                className={`flex-1 ${bulkAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                onClick={handleBulkAction}
                disabled={bulkProcessing || (bulkAction === 'reject' && !bulkComments.trim())}
              >
                {bulkProcessing ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    {bulkAction === 'approve' ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Confirm {bulkAction === 'approve' ? 'Approval' : 'Rejection'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TeamRegularizationPage;
